import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '../../../ui-library/typography/typography';
import type { ChatHistory } from '../../flora/flora.type';

interface AgentSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatHistories: ChatHistory[];
  onResultClick: (chatId: string) => void;
}

interface SearchResult {
  chatId: string;
  chatDescription: string;
  messageContent: string;
  role: 'user' | 'assistant';
  timestamp?: string;
}

export function AgentSearchModal({ isOpen, onClose, chatHistories, onResultClick }: AgentSearchModalProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo<SearchResult[]>(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return [];

    const matches: SearchResult[] = [];

    for (const chat of chatHistories) {
      for (const msg of chat.messages) {
        const content = typeof msg.content === 'string' ? msg.content : '';
        if (content.toLowerCase().includes(trimmed)) {
          matches.push({
            chatId: chat.id,
            chatDescription: chat.description || 'Untitled',
            messageContent: content,
            role: msg.role as 'user' | 'assistant',
            timestamp: msg.timestamp,
          });
        }
      }
    }

    return matches.slice(0, 50);
  }, [query, chatHistories]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore
  useEffect(() => {
    setActiveIndex(0);
  }, [results]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Keyboard shortcut to open (Cmd+K)
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        if (isOpen) {
          e.preventDefault();
          onClose();
        }
        // do not preventDefault if not open, to avoid blocking browser/app shortcuts
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [isOpen, onClose]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      onResultClick(result.chatId);
      onClose();
    },
    [onResultClick, onClose],
  );

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[activeIndex]) {
      handleSelect(results[activeIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Scroll active item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.children[activeIndex] as HTMLElement | undefined;
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!isOpen) return null;

  const highlightMatch = (text: string): React.ReactNode => {
    const trimmed = query.trim();
    if (!trimmed) return text;
    const idx = text.toLowerCase().indexOf(trimmed.toLowerCase());
    if (idx === -1) return text;

    // Show a window around the match
    const windowSize = 60;
    const start = Math.max(0, idx - windowSize);
    const end = Math.min(text.length, idx + trimmed.length + windowSize);
    const prefix = start > 0 ? '...' : '';
    const suffix = end < text.length ? '...' : '';
    const before = text.slice(start, idx);
    const match = text.slice(idx, idx + trimmed.length);
    const after = text.slice(idx + trimmed.length, end);

    return (
      <>
        {prefix}
        {before}
        <Highlight>{match}</Highlight>
        {after}
        {suffix}
      </>
    );
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
        <SearchInputRow>
          <SearchIcon icon="material-symbols:search" width={20} height={20} />
          <SearchInput
            ref={inputRef}
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search conversations..."
            autoComplete="off"
            spellCheck={false}
          />
          <Kbd>ESC</Kbd>
        </SearchInputRow>

        {query.trim() && (
          <ResultsArea ref={listRef}>
            {results.length === 0 ? (
              <EmptyState>
                <Text size="small" color="var(--ink-lighter)">
                  No results found
                </Text>
              </EmptyState>
            ) : (
              results.map((result, i) => (
                <ResultRow
                  key={`${result.chatId}-${i}`}
                  $active={i === activeIndex}
                  onClick={() => handleSelect(result)}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <ResultIcon>
                    <Icon
                      icon={
                        result.role === 'user'
                          ? 'material-symbols:person-outline'
                          : 'material-symbols:smart-toy-outline'
                      }
                      width={16}
                      height={16}
                    />
                  </ResultIcon>
                  <ResultContent>
                    <ResultChatName size="tiny" weight="bold" color="var(--ink-lighter)">
                      {result.chatDescription}
                    </ResultChatName>
                    <ResultSnippet size="small" color="var(--ink-dark)">
                      {highlightMatch(result.messageContent)}
                    </ResultSnippet>
                  </ResultContent>
                </ResultRow>
              ))
            )}
          </ResultsArea>
        )}
      </ModalContainer>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: var(--flora-overlay-bg);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
`;

const ModalContainer = styled.div`
  width: 640px;
  max-width: 90vw;
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 12px;
  box-shadow: 0 16px 70px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 480px;
`;

const SearchInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--sky-light);
`;

const SearchIcon = styled(Icon)`
  color: var(--ink-lighter);
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: var(--ink-darkest);
  font-family: inherit;

  &::placeholder {
    color: var(--ink-lighter);
  }
`;

const Kbd = styled.span`
  font-size: 11px;
  font-family: inherit;
  color: var(--ink-lighter);
  background: var(--flora-hover-subtle);
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  padding: 2px 6px;
  flex-shrink: 0;
  user-select: none;
`;

const ResultsArea = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 6px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
`;

const ResultRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'var(--flora-hover-medium)' : 'transparent')};

  &:hover {
    background: var(--flora-hover-medium);
  }
`;

const ResultIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--flora-hover-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--ink-lighter);
  margin-top: 1px;
`;

const ResultContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ResultChatName = styled(Text)`
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const ResultSnippet = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const Highlight = styled.mark`
  background: rgba(87, 92, 229, 0.2);
  color: inherit;
  border-radius: 2px;
  padding: 0 1px;
`;
